import type {
  InternalEmailDocument,
  InternalEmailDocuments,
} from '@/api/internalEmails';

/**
 * What the sender picked in the attach-files popup.
 *
 * Existing candidate documents are held as *references* — an id and a name, no
 * file contents — and are only read server-side when the email goes out. Only
 * `uploads` carries real bytes, because those files came from this device.
 */
export interface AttachmentSelection {
  atsAttachmentIds: number[];
  offerDocumentBlobIds: number[];
  includeResume: boolean;
  includeScorecards: boolean;
  uploads: File[];
}

export const EMPTY_SELECTION: AttachmentSelection = {
  atsAttachmentIds: [],
  offerDocumentBlobIds: [],
  includeResume: false,
  includeScorecards: false,
  uploads: [],
};

export const RESUME_KEY = 'resume';
export const SCORECARDS_KEY = 'scorecards';

/** A chip below the composer. `key` identifies it for removal. */
export interface AttachmentChip {
  key: string;
  name: string;
}

export const offerKey = (blobId: number) => `offer:${blobId}`;
export const attachmentKey = (id: number) => `attachment:${id}`;
const uploadKey = (index: number) => `upload:${index}`;

export const OFFER_DOCUMENTS_GROUP = 'Offer documents';
export const CANDIDATE_DETAILS_GROUP = 'Candidate details';
export const OTHER_FILES_GROUP = 'Other files';

const SCORECARDS_LABEL = 'All previous scorecards';
const RESUME_LABEL = "Candidate's resume";

export interface AttachOption {
  value: string;
  label: string;
  group: string;
}

/**
 * The candidate's documents as picker rows, grouped and in the order the
 * headings are drawn — the control neither sorts nor regroups, and reports a
 * committed selection back in this same order.
 *
 * Values are the chip keys, so one vocabulary identifies a document whether it
 * is being picked, listed or removed.
 */
export const toAttachOptions = (
  documents: InternalEmailDocuments | undefined
): AttachOption[] => {
  const options: AttachOption[] = [];

  (documents?.offer_documents ?? []).forEach((document) => {
    if (document.blob_id === undefined) return;
    options.push({
      value: offerKey(document.blob_id),
      label: document.filename ?? 'Offer document',
      group: OFFER_DOCUMENTS_GROUP,
    });
  });

  if (documents?.scorecards_available) {
    options.push({
      value: SCORECARDS_KEY,
      label: SCORECARDS_LABEL,
      group: CANDIDATE_DETAILS_GROUP,
    });
  }

  if (documents?.resume) {
    options.push({
      value: RESUME_KEY,
      label: documents.resume.filename ?? RESUME_LABEL,
      group: CANDIDATE_DETAILS_GROUP,
    });
  }

  (documents?.other_files ?? []).forEach((document) => {
    if (document.ats_attachment_id === undefined) return;
    options.push({
      value: attachmentKey(document.ats_attachment_id),
      label: document.filename ?? 'Document',
      group: OTHER_FILES_GROUP,
    });
  });

  return options;
};

/** The picked documents as picker values. Uploads are not rows, so not here. */
export const toSelectedValues = (selection: AttachmentSelection): string[] => [
  ...selection.offerDocumentBlobIds.map(offerKey),
  ...(selection.includeScorecards ? [SCORECARDS_KEY] : []),
  ...(selection.includeResume ? [RESUME_KEY] : []),
  ...selection.atsAttachmentIds.map(attachmentKey),
];

/**
 * A committed picker selection back into the send's shape, keeping the uploads
 * the picker never owned.
 */
export const fromSelectedValues = (
  values: string[],
  selection: AttachmentSelection
): AttachmentSelection => ({
  offerDocumentBlobIds: values
    .filter((value) => value.startsWith('offer:'))
    .map((value) => Number(value.slice('offer:'.length))),
  atsAttachmentIds: values
    .filter((value) => value.startsWith('attachment:'))
    .map((value) => Number(value.slice('attachment:'.length))),
  includeResume: values.includes(RESUME_KEY),
  includeScorecards: values.includes(SCORECARDS_KEY),
  uploads: selection.uploads,
});

/**
 * The chips shown for a selection, in a stable order so removing one does not
 * reshuffle the rest.
 */
export const toAttachmentChips = (
  selection: AttachmentSelection,
  documents: InternalEmailDocuments | undefined
): AttachmentChip[] => {
  const chips: AttachmentChip[] = [];
  const byBlobId = new Map<number, InternalEmailDocument>();
  const byAttachmentId = new Map<number, InternalEmailDocument>();

  (documents?.offer_documents ?? []).forEach((document) => {
    if (document.blob_id !== undefined)
      byBlobId.set(document.blob_id, document);
  });
  (documents?.other_files ?? []).forEach((document) => {
    if (document.ats_attachment_id !== undefined) {
      byAttachmentId.set(document.ats_attachment_id, document);
    }
  });

  selection.offerDocumentBlobIds.forEach((blobId) => {
    chips.push({
      key: offerKey(blobId),
      name: byBlobId.get(blobId)?.filename ?? 'Offer document',
    });
  });

  if (selection.includeResume) {
    chips.push({
      key: RESUME_KEY,
      name: documents?.resume?.filename ?? 'Resume',
    });
  }

  if (selection.includeScorecards) {
    chips.push({ key: SCORECARDS_KEY, name: 'All previous scorecards' });
  }

  selection.atsAttachmentIds.forEach((id) => {
    chips.push({
      key: attachmentKey(id),
      name: byAttachmentId.get(id)?.filename ?? 'Document',
    });
  });

  selection.uploads.forEach((file, index) => {
    chips.push({ key: uploadKey(index), name: file.name });
  });

  return chips;
};

/** Drops whatever a chip's ✕ refers to, leaving the rest of the selection intact. */
export const removeAttachment = (
  selection: AttachmentSelection,
  key: string
): AttachmentSelection => {
  if (key === RESUME_KEY) return { ...selection, includeResume: false };
  if (key === SCORECARDS_KEY) return { ...selection, includeScorecards: false };

  if (key.startsWith('offer:')) {
    const blobId = Number(key.slice('offer:'.length));
    return {
      ...selection,
      offerDocumentBlobIds: selection.offerDocumentBlobIds.filter(
        (id) => id !== blobId
      ),
    };
  }

  if (key.startsWith('attachment:')) {
    const attachmentId = Number(key.slice('attachment:'.length));
    return {
      ...selection,
      atsAttachmentIds: selection.atsAttachmentIds.filter(
        (id) => id !== attachmentId
      ),
    };
  }

  if (key.startsWith('upload:')) {
    const index = Number(key.slice('upload:'.length));
    return {
      ...selection,
      uploads: selection.uploads.filter((_file, i) => i !== index),
    };
  }

  return selection;
};

/** Maps the selection onto the send payload's attachment fields. */
export const toAttachmentPayload = (selection: AttachmentSelection) => ({
  ats_attachment_ids: selection.atsAttachmentIds,
  offer_document_blob_ids: selection.offerDocumentBlobIds,
  include_resume: selection.includeResume,
  include_scorecards: selection.includeScorecards,
  attachments: selection.uploads,
});
